import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Typography } from "@/components/atoms";
import { Badge } from "@/components/atoms";
import { Loading } from "@/components/atoms/Loading/Loading";
import {
  ComparePlans,
  PageHeader,
  useSyncCardHeights, // TODO: Uncomment when View Demo API is available
  // ViewDemoModal,
  // useViewDemoModal,
  AverageMonthlyBookingPanel,
  AccountOwnerAccessRequiredModal,
} from "@/components/organisms";
import { SubscriptionCard } from "@/components/organisms/SubscriptionPlan/SubscriptionCard";
import {
  ChoosePlanPaymentModal,
  CannotDowngradeModal,
  ReviewPlanChangesModal,
  ConfirmDowngradeModal,
  DowngradeSuccessModal,
  UpgradeSuccessModal,
} from "@/components/organisms/SubscriptionPlan/modals";
import { useOperator, useSubscriptionAssignment, useSubscriptionPlanLimits } from "@/hooks";
import { useGetFleetDriversQuery } from "@/redux/apis/driver/driverApi";
import {
  useGetSubscriptionPlansQuery,
  useGetProviderSubscriptionsQuery,
  useChangeSubscriptionPlanMutation,
  useCancelScheduledPlanChangeMutation,
} from "@/redux/apis/subscription/subscriptionPlansApi";
import { SubscriptionPlan as SubscriptionPlanType } from "@/redux/apis/subscription/types";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { useAppSelector } from "@/redux/hooks";
import { fleetDriversPageSelector } from "@/redux/slices/driver/selectors";
import { addToast } from "@/utils";

export const SubscriptionPlanPage = () => {
  const { getPlanLimits } = useSubscriptionPlanLimits();
  const operator = useOperator();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const navigate = useNavigate();
  const { subscriptionStatus } = useSubscriptionAssignment();
  const isExistingUser = subscriptionStatus.userType === "existing";
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: subscriptionData, isLoading, error } = useGetSubscriptionPlansQuery();
  const { data: providerSubscriptions } = useGetProviderSubscriptionsQuery({
    providerId: operator.id,
    status: ["active", "pending_cancellation", "trial", "overdue"],
    limit: 1,
    orderBy: ["startDate:DESC"],
  });
  const [changeSubscriptionPlan, { isLoading: isChangingPlan }] = useChangeSubscriptionPlanMutation();
  const [cancelScheduledChange, { isLoading: isCancellingChange }] = useCancelScheduledPlanChangeMutation();
  // TODO: Uncomment when View Demo API is available
  // const { isOpen, activePlan, openModal, closeModal } = useViewDemoModal();
  const [isChoosePlanModalOpen, setIsChoosePlanModalOpen] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState<SubscriptionPlanType | null>(null);
  const [cannotDowngradeModalOpen, setCannotDowngradeModalOpen] = useState(false);
  const [downgradeModalData, setDowngradeModalData] = useState({
    planName: "",
    currentVehicles: 0,
    allowedVehicles: 0,
    currentDrivers: 0,
    allowedDrivers: 0,
    exceedsVehicles: false,
    exceedsDrivers: false,
  });

  // Downgrade flow modal states
  const [reviewPlanChangesModalOpen, setReviewPlanChangesModalOpen] = useState(false);
  const [confirmDowngradeModalOpen, setConfirmDowngradeModalOpen] = useState(false);
  const [downgradeSuccessModalOpen, setDowngradeSuccessModalOpen] = useState(false);
  const [targetDowngradePlan, setTargetDowngradePlan] = useState<SubscriptionPlanType | null>(null);

  // Upgrade flow modal states
  const [upgradeSuccessModalOpen, setUpgradeSuccessModalOpen] = useState(false);
  const [targetUpgradePlan, setTargetUpgradePlan] = useState<SubscriptionPlanType | null>(null);

  // Get current vehicle count
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useGetVehiclesQuery(
    {
      owner_uuid: operator.id ?? "",
      page_size: "1",
      page: "1",
    },
    {
      skip: !operator.id,
    }
  );

  // Get current driver count
  const { isLoading: isLoadingDrivers } = useGetFleetDriversQuery(
    {
      provider_uuid: operator.id ?? "",
      page: "1",
      page_size: "1",
    },
    {
      skip: !operator.id,
    }
  );

  const vehicleCount = vehiclesData?.page.total ?? 0;
  const driverCount = useAppSelector(fleetDriversPageSelector).total ?? 0;

  // Check if we're still loading the counts needed for validation
  const isLoadingCounts = isLoadingVehicles || isLoadingDrivers;

  // Check for success message from query params after reload
  useEffect(() => {
    const success = searchParams.get("success");
    const planId = searchParams.get("planId");

    if (success && subscriptionData) {
      switch (success) {
        case "upgrade":
          // Find the upgraded plan from subscription data and show the modal
          if (planId) {
            const upgradedPlan = subscriptionData.subscriptions.find((s) => s.subscriptionId === planId);
            if (upgradedPlan) {
              setTargetUpgradePlan(upgradedPlan);
            }
          }
          setUpgradeSuccessModalOpen(true);
          break;
        case "reactivate":
          addToast("success", "Subscription reactivated successfully");
          break;
        case "cancel":
          addToast("success", "Plan change cancelled successfully");
          break;
        case "downgrade":
          // Find the downgraded plan from subscription data and show the modal
          if (planId) {
            const downgradedPlan = subscriptionData.subscriptions.find((s) => s.subscriptionId === planId);
            if (downgradedPlan) {
              setTargetDowngradePlan(downgradedPlan);
            }
          }
          setDowngradeSuccessModalOpen(true);
          break;
      }
      // Clean up URL by removing query param
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, subscriptionData]);

  // Get current plan and scheduled change from provider's active subscriptions
  const activeSubscription = providerSubscriptions?.[0];
  const currentPlan = activeSubscription?.subscription?.subscriptionId;
  const currentPlanDisplayName = activeSubscription?.subscription?.name;
  const scheduledPlanChange = activeSubscription?.scheduledPlanChange;
  const isPendingCancellation = activeSubscription?.status === "pending_cancellation";
  const cancellationDate = activeSubscription?.billingCycleEnd;
  const hasNoSubscription = !activeSubscription;

  // Get dynamic refs for however many subscriptions we have
  const numSubscriptions = subscriptionData?.subscriptions.length || 3;
  const cardRefs = useSyncCardHeights(numSubscriptions, !isLoading && !!subscriptionData);

  // Map subscription IDs to display properties
  const subscriptionDisplayConfig = useMemo(() => {
    if (!subscriptionData) return {};

    const config: Record<string, { showRecommended: boolean; showMostPopular: boolean }> = {};

    // Map based on subscription IDs
    subscriptionData.subscriptions.forEach((sub) => {
      if (sub.subscriptionId === "starter-rider-plan") {
        config[sub.subscriptionId] = { showRecommended: true, showMostPopular: false };
      } else if (sub.subscriptionId === "basic-provider-plan") {
        config[sub.subscriptionId] = { showRecommended: false, showMostPopular: true };
      } else {
        config[sub.subscriptionId] = { showRecommended: false, showMostPopular: false };
      }
    });

    return config;
  }, [subscriptionData]);

  const handleDowngrade = (subscriptionId: string) => {
    // Existing users (no payment method) — redirect to subscriptions page to add payment method
    if (isExistingUser) {
      navigate("/payments/subscriptions");
      return;
    }

    if (!activeSubscription) {
      addToast("danger", "No active subscription to downgrade");
      return;
    }

    // Wait for vehicle and driver counts to load before validating
    if (isLoadingCounts) {
      addToast("info", "Loading your account information...");
      return;
    }

    // Get the target plan to check limits
    const targetPlan = subscriptionData?.subscriptions.find((s) => s.subscriptionId === subscriptionId);
    if (!targetPlan) {
      addToast("danger", "Target subscription plan not found");
      return;
    }

    // Get plan limits using the slug from the subscription data
    const targetLimits = getPlanLimits(targetPlan.slug);

    // Check if current counts exceed target plan limits
    const exceedsVehicles = vehicleCount > targetLimits.maxVehicles;
    const exceedsDrivers = driverCount > targetLimits.maxDrivers;

    if (exceedsVehicles || exceedsDrivers) {
      // Show cannot downgrade modal
      setDowngradeModalData({
        planName: targetPlan.subscriptionName,
        currentVehicles: vehicleCount,
        allowedVehicles: targetLimits.maxVehicles,
        currentDrivers: driverCount,
        allowedDrivers: targetLimits.maxDrivers,
        exceedsVehicles,
        exceedsDrivers,
      });
      setCannotDowngradeModalOpen(true);
      return;
    }

    // Within limits - start the downgrade flow with ReviewPlanChangesModal
    setTargetDowngradePlan(targetPlan);
    setReviewPlanChangesModalOpen(true);
  };

  // Called when user clicks "Continue" on ReviewPlanChangesModal
  const handleReviewPlanChangesContinue = () => {
    setReviewPlanChangesModalOpen(false);
    setConfirmDowngradeModalOpen(true);
  };

  // Called when user clicks "Confirm Downgrade" on ConfirmDowngradeModal
  const handleConfirmDowngrade = async () => {
    if (!activeSubscription || !targetDowngradePlan) {
      addToast("danger", "Unable to process downgrade");
      return;
    }

    try {
      await changeSubscriptionPlan({
        providerId: operator.id,
        subscriptionAssignmentId: activeSubscription.subscriptionAssignmentId,
        newSubscriptionId: targetDowngradePlan.subscriptionId,
      }).unwrap();
      // Reload with success query param and planId - modal will be shown after reload
      window.location.href = window.location.pathname + `?success=downgrade&planId=${targetDowngradePlan.subscriptionId}`;
    } catch (error) {
      addToast("danger", "Failed to downgrade subscription plan");
    }
  };

  // Called when user closes DowngradeSuccessModal
  const handleDowngradeSuccessClose = () => {
    setDowngradeSuccessModalOpen(false);
    setTargetDowngradePlan(null);
  };

  // Called when user closes UpgradeSuccessModal
  const handleUpgradeSuccessClose = () => {
    setUpgradeSuccessModalOpen(false);
    setTargetUpgradePlan(null);
  };

  const handleSubscribe = (subscriptionId: string) => {
    // Existing users (no payment method) — redirect to subscriptions page to add payment method
    if (isExistingUser) {
      navigate("/payments/subscriptions");
      return;
    }

    const plan = subscriptionData?.subscriptions.find((s) => s.subscriptionId === subscriptionId);
    if (plan) {
      setSelectedPlanForModal(plan);
      setIsChoosePlanModalOpen(true);
    }
  };

  const handleUpgrade = async (subscriptionId: string) => {
    // Existing users (no payment method) — redirect to subscriptions page to add payment method
    if (isExistingUser) {
      navigate("/payments/subscriptions");
      return;
    }

    if (!activeSubscription) {
      // No active subscription - redirect to subscribe flow
      handleSubscribe(subscriptionId);
      return;
    }

    try {
      await changeSubscriptionPlan({
        providerId: operator.id,
        subscriptionAssignmentId: activeSubscription.subscriptionAssignmentId,
        newSubscriptionId: subscriptionId,
      }).unwrap();
      // Reload with success query param and planId to show correct plan in modal
      window.location.href = window.location.pathname + `?success=upgrade&planId=${subscriptionId}`;
    } catch (error) {
      addToast("danger", "Failed to schedule subscription plan change");
    }
  };

  const handleReactivate = async (subscriptionId: string) => {
    // Existing users (no payment method) — redirect to subscriptions page to add payment method
    if (isExistingUser) {
      navigate("/payments/subscriptions");
      return;
    }

    if (!activeSubscription) {
      addToast("danger", "No subscription to reactivate");
      return;
    }

    try {
      await changeSubscriptionPlan({
        providerId: operator.id,
        subscriptionAssignmentId: activeSubscription.subscriptionAssignmentId,
        newSubscriptionId: subscriptionId,
      }).unwrap();
      // Reload with success query param
      window.location.href = window.location.pathname + "?success=reactivate";
    } catch (error) {
      addToast("danger", "Failed to reactivate subscription");
    }
  };

  const handleCancelScheduledChange = async () => {
    if (!activeSubscription) {
      addToast("danger", "No active subscription found");
      return;
    }

    try {
      await cancelScheduledChange({
        providerId: operator.id,
        subscriptionAssignmentId: activeSubscription.subscriptionAssignmentId,
      }).unwrap();
      // Reload with success query param
      window.location.href = window.location.pathname + "?success=cancel";
    } catch (error) {
      addToast("danger", "Failed to cancel scheduled plan change");
    }
  };

  // Get current plan name for display
  const currentPlanName = useMemo(() => {
    if (!subscriptionData || !currentPlan) return "No active plan";
    const plan = subscriptionData.subscriptions.find((s) => s.subscriptionId === currentPlan);
    return plan?.subscriptionName || "No active plan";
  }, [subscriptionData, currentPlan]);

  const isOwnerOrFa = operator.roles.includes("Owner") || operator.roles.includes("Fa_Access");
  if (!isOwnerOrFa) return <AccountOwnerAccessRequiredModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />;

  if (isLoading) {
    return (
      <Container className="flex w-full max-w-[1040px] flex-1 flex-col items-center justify-center p-5">
        <Loading />
      </Container>
    );
  }

  if (error || !subscriptionData) {
    return (
      <Container className="flex w-full max-w-[1040px] flex-1 flex-col items-center justify-center p-5">
        <Typography variant="h3" className="text-error-primary">
          Error loading subscription plans
        </Typography>
        <Typography variant="paragraph" className="mt-2">
          Please try again later
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <div className="sticky top-0 z-20 -mx-3 border-b border-neutral-mid-gray bg-white lg:-mx-5">
        <Container className="w-full">
          <PageHeader.Actions className="px-3 pb-0 pt-3 lg:px-5" />
          <PageHeader className="!mx-0 px-3 lg:px-5" title="Subscription Plan" skipCrumbs={1} />
        </Container>
      </div>
      <div className="-mx-3 flex-1 bg-neutral-surface-gray lg:-mx-5">
        <div className="px-3 py-6 lg:px-5">
          <Container className="w-full">
            <AverageMonthlyBookingPanel />
          </Container>
        </div>
        <div className="px-3 py-6 lg:px-5">
          <Container className="w-full">
            <div className="mt-6 flex flex-col text-neutral-dark-gray">
              <div className="flex w-full flex-col items-center gap-1 text-center">
                <Typography variant="h2">Upgrade your plan for more features</Typography>
                <div className="flex items-center gap-2">
                  <Typography variant="paragraph">
                    Current Plan: {isPendingCancellation ? "No active plan" : currentPlanName}
                    {isPendingCancellation && currentPlanDisplayName && (
                      <span className="text-danger">
                        {" "}
                        (Previous {currentPlanDisplayName} plan cancels on{" "}
                        {cancellationDate
                          ? new Date(cancellationDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                          : ""}
                        )
                      </span>
                    )}
                  </Typography>
                  {activeSubscription?.isTrialPeriod && activeSubscription?.trialEndsAt && (
                    <Badge
                      type="info"
                      size="sm"
                      message={`Trial period ends ${new Date(activeSubscription.trialEndsAt).toLocaleDateString("en-AU", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}`}
                    />
                  )}
                </div>
              </div>
              <div className="mt-10 grid w-full grid-cols-1 gap-5 lg:grid-cols-3">
                {subscriptionData.subscriptions.map((subscription, index) => {
                  const refs = cardRefs[index] || { headerRef: null, coreRef: null };
                  const displayConfig = subscriptionDisplayConfig[subscription.subscriptionId] || {
                    showRecommended: false,
                    showMostPopular: false,
                  };

                  return (
                    <SubscriptionCard
                      key={subscription.subscriptionId}
                      subscription={subscription}
                      allSubscriptionData={subscriptionData}
                      headerRef={refs.headerRef}
                      coreRef={refs.coreRef}
                      // TODO: Uncomment when View Demo API is available
                      // onViewDemo={openModal}
                      currentPlan={currentPlan}
                      scheduledPlanChange={scheduledPlanChange}
                      onDowngrade={handleDowngrade}
                      onUpgrade={handleUpgrade}
                      onSubscribe={handleSubscribe}
                      onReactivate={handleReactivate}
                      onCancelScheduledChange={handleCancelScheduledChange}
                      showRecommended={displayConfig.showRecommended}
                      showMostPopular={displayConfig.showMostPopular}
                      isChangingPlan={isChangingPlan || isCancellingChange || isLoadingCounts}
                      isPendingCancellation={isPendingCancellation}
                      cancellationDate={cancellationDate}
                      hasNoSubscription={hasNoSubscription}
                    />
                  );
                })}
              </div>
            </div>
            <ComparePlans
              className="mt-16"
              currentPlan={currentPlan}
              scheduledPlanChange={scheduledPlanChange}
              onDowngrade={handleDowngrade}
              onUpgrade={handleUpgrade}
              onSubscribe={handleSubscribe}
              onReactivate={handleReactivate}
              onCancelScheduledChange={handleCancelScheduledChange}
              // TODO: Uncomment when View Demo API is available
              // onViewDemo={openModal}
              isChangingPlan={isChangingPlan || isCancellingChange || isLoadingCounts}
              hasNoSubscription={hasNoSubscription}
              isPendingCancellation={isPendingCancellation}
            />
            {/* TODO: Uncomment when View Demo API is available */}
            {/* <ViewDemoModal open={isOpen} onClose={closeModal} plan={activePlan} /> */}
            <ChoosePlanPaymentModal
              open={isChoosePlanModalOpen}
              onClose={() => {
                setIsChoosePlanModalOpen(false);
                setSelectedPlanForModal(null);
              }}
              selectedPlan={selectedPlanForModal}
            />
            <CannotDowngradeModal
              open={cannotDowngradeModalOpen}
              onClose={() => setCannotDowngradeModalOpen(false)}
              data={downgradeModalData}
            />
            <ReviewPlanChangesModal
              open={reviewPlanChangesModalOpen}
              onClose={() => {
                setReviewPlanChangesModalOpen(false);
                setTargetDowngradePlan(null);
              }}
              onContinue={handleReviewPlanChangesContinue}
              currentPlanId={currentPlan}
              newPlanId={targetDowngradePlan?.subscriptionId}
              subscriptionData={subscriptionData}
            />
            <ConfirmDowngradeModal
              open={confirmDowngradeModalOpen}
              onClose={() => {
                setConfirmDowngradeModalOpen(false);
                setTargetDowngradePlan(null);
              }}
              onConfirm={handleConfirmDowngrade}
              targetPlan={targetDowngradePlan ?? undefined}
              subscriptionData={subscriptionData}
              isLoading={isChangingPlan}
            />
            <DowngradeSuccessModal open={downgradeSuccessModalOpen} onClose={handleDowngradeSuccessClose} operatorName={operator.name} />
            <UpgradeSuccessModal
              open={upgradeSuccessModalOpen}
              onClose={handleUpgradeSuccessClose}
              operatorName={operator.name}
              targetPlan={targetUpgradePlan ?? undefined}
              subscriptionData={subscriptionData}
            />
          </Container>
        </div>
      </div>
    </>
  );
};
